/**
 * Shared layout + detection for spreadsheet previews inside flex fill regions
 * (Favorites drawer, evidence locker overlay, etc.).
 */

export type SpreadsheetMediaLike = {
  mediaType?: string | null;
  srcToUse?: string;
  previewFilePath?: string;
  mediaUrl?: string;
  mediaSrc?: string;
  originalUrl?: string;
  sourceUrl?: string;
  sourceFilePath?: string;
};

export function spreadsheetContentUrl(item: SpreadsheetMediaLike | null | undefined): string {
  return (
    item?.srcToUse ||
    item?.previewFilePath ||
    item?.mediaUrl ||
    item?.mediaSrc ||
    item?.originalUrl ||
    item?.sourceUrl ||
    item?.sourceFilePath ||
    ''
  );
}

export function isSpreadsheetMediaItem(
  item: SpreadsheetMediaLike | null | undefined,
  options?: { viewAsCsvLike?: boolean },
): boolean {
  if (!item) return false;
  if (options?.viewAsCsvLike) return true;
  if (item.mediaType === 'spreadsheet') return true;
  const pathOnly = spreadsheetContentUrl(item).split(/[?#]/)[0].toLowerCase();
  return /\.(xls|xlsx|csv|tsv)$/.test(pathOnly);
}

/** Spreadsheet in a flex fill slot needs a bounded height + internal scroll. */
export function shouldBoundSpreadsheetInFillContainer(
  fillContainer: boolean | undefined,
  item: SpreadsheetMediaLike | null | undefined,
  options?: { viewAsCsvLike?: boolean },
): boolean {
  return Boolean(fillContainer && isSpreadsheetMediaItem(item, options));
}

export const BOUNDED_SPREADSHEET_SLOT_CLASS =
  'flex min-h-0 min-w-0 w-full max-w-full flex-1 flex-col self-stretch overflow-hidden';

// overflow-x-auto (not hidden): a rotated/zoomed video or an oversized image inside
// this slot can be wider than the slot too, not just taller — without horizontal
// scroll that excess was unreachable exactly like the vertical case this class
// already handled (Evidence Locker "can't scroll media file in viewer" prod bug).
export const FILL_CONTAINER_SCROLL_SLOT_CLASS =
  'flex-1 min-h-0 overflow-auto custom-scrollbar2';

export const DEFAULT_MEDIA_PREVIEW_SLOT_CLASS =
  'flex min-w-0 w-full max-w-full shrink-0 flex-col self-stretch';

/**
 * Answer-item sources scrollport may reserve bottom padding for legacy layouts.
 * Below-stack split bar height is accounted in ``BELOW_STACK_SPLITTER_TOTAL_PX``.
 */
export const ANSWER_ITEM_VIDEO_CARD_HEIGHT_SLACK_PX = 20;

export function mediaPreviewSlotClassName(
  fillContainer: boolean | undefined,
  item: SpreadsheetMediaLike | null | undefined,
  options?: { viewAsCsvLike?: boolean; extraClassName?: string },
): string {
  const extra = options?.extraClassName?.trim() ?? '';
  const bound = shouldBoundSpreadsheetInFillContainer(fillContainer, item, options);
  const base = bound
    ? BOUNDED_SPREADSHEET_SLOT_CLASS
    : fillContainer
      ? FILL_CONTAINER_SCROLL_SLOT_CLASS
      : DEFAULT_MEDIA_PREVIEW_SLOT_CLASS;
  return extra ? `${base} ${extra}` : base;
}

export function mediaPreviewSlotStyle(
  fillContainer: boolean | undefined,
  item: SpreadsheetMediaLike | null | undefined,
  options?: {
    viewAsCsvLike?: boolean;
    minHeight?: number;
    isContinuousMedia?: boolean;
  },
): Record<string, string | number> | undefined {
  const bound = shouldBoundSpreadsheetInFillContainer(fillContainer, item, options);
  if (bound) {
    return {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      flex: '1 1 0',
      minHeight: 0,
    };
  }
  if (fillContainer) {
    return undefined;
  }
  const style: Record<string, string | number> = {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    flex: '0 0 auto',
  };
  if (
    options?.minHeight &&
    options.minHeight > 0 &&
    !options.isContinuousMedia
  ) {
    style.minHeight = options.minHeight;
  }
  return style;
}
